import _ from 'lodash';

export const getAdjustedChat = (chat, aggregationType = 'SQUARED') => {
  /*
    Takes an array of chat entries and modifies the emotional statuses
    based on previous values.

    Aggregation Types:
      LINEAR: all previous chat values hold equal weight
      SQUARED: current value holds 50% weight, previous 25%, 12.5% and so on
      RECOMMENDED: custom adjustments
      DISABLED: only current value holds weight
  */

  let adjustedChat = _.map(chat, (entry, idx) => {
    const isYou = entry.isYou;
    const emotionalState = isYou ? entry.you : entry.them;
    const previousComments = chat.slice(0, idx).filter((c) => c.isYou === isYou);

    /*
      First build a mapping of emotions to past values. For example, if there are four comments
      before the one we are currently adjusting, it would return:
        {
          'joy': [1, 0.4, 0.8],
          'sadness': [0.4, 0.8, 0.3],
          ...
        }
    */
    const previousEmotionRatings = _.reduce(
      previousComments,
      (result, prevEntry) => {
        const data = isYou ? prevEntry.you : prevEntry.them;
        return _.reduce(
          result,
          (res, val, key) => ({
            ...res,
            [key]: [...val, data[key]],
          }),
          {}
        );
      },
      _.reduce(
        emotionalState,
        (res, val, key) => ({
          [key]: [],
          ...res,
        }),
        {}
      )
    );

    /*
      Add the current value to the end of the array
        {
          'joy': [1, 0.4, 0.8, 0.5],
          'sadness': [0.4, 0.8, 0.3, 0.4],
          ...
        }
    */
    const allEmotionRankings = _.reduce(
      previousEmotionRatings,
      (res, val, key) => ({
        ...res,
        [key]: [...val, emotionalState[key]],
      }),
      {}
    );

    /*
      Aggregate the previous and current value into a single ranking
        {
          'joy': 0.65
          'sadness': 0.4,
          ...
        }
    */
    const adjustedEmotionalRankings = _.reduce(
      allEmotionRankings,
      (res, val, key) => {
        let adjValue = _.sum(
          val.map((level, idx) => {
            switch (aggregationType) {
              case 'DISABLED':
                return idx === val.length - 1 ? level : 0;
              case 'LINEAR':
                return level / val.length;
              case 'SQUARED':
                return (level * Math.pow(0.5, val.length - idx)) / (1 - Math.pow(0.5, val.length));
              case 'RECOMMENDED':
                return (level * Math.pow(0.8, val.length - idx)) / ((0.8 * (Math.pow(0.8, val.length) - 1)) / (0.8 - 1));
              default:
                return 0;
            }
          })
        );

        return {
          ...res,
          [key]: adjValue,
        };
      },
      {}
    );

    return {
      ...entry,
      [isYou ? 'you' : 'them']: adjustedEmotionalRankings,
    };
  });
  return adjustedChat;
};

export const transformMessagesFromServer = (messages) => {
  /*
    Perform calculations upon response from the server before passing through to the
    reducer.
  */

  const orderedMessages = _.orderBy(messages, 'start_time_seconds');
  return orderedMessages.map((msg, idx) => {
    const is_customer = msg.speaker.speaker_type === 'customer';
    const is_rep = msg.speaker.speaker_type === 'rep';
    const summary = _.get(msg, 'summary.0.0') || _.get(msg, 'summary.0') || _.get(msg, 'summary') || {};

    // Server is inconsistent in formatting, handle issues here // UNNEEDED_SUMMARY_ARRAY
    const getSentimentPositiveFromMessage = (message) => {
      const summary = _.get(message, 'summary.0.0') || _.get(message, 'summary.0') || _.get(message, 'summary') || {}; // UNNEEDED_SUMMARY_ARRAY
      return _.get(summary, 'prob_sentiment_positive') || 0;
    };

    let smoothWindow = [10];
    if (orderedMessages.length > 250) {
      smoothWindow = [
        35,
        34,
        33,
        32,
        31,
        30,
        29,
        28,
        27,
        26,
        25,
        24,
        23,
        22,
        21,
        20,
        19,
        18,
        17,
        16,
        15,
        14,
        13,
        12,
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        2,
        1,
      ];
    } else if (orderedMessages.length > 150) {
      smoothWindow = [15, 14, 13, 12, 10, 8, 6, 4, 2];
      smoothWindow = [10, 8, 6, 4, 2];
    } else if (orderedMessages.length > 75) {
      smoothWindow = [10, 6, 2];
    }

    // Smooth the values - TODO move this server side
    const smoothedPositiveSentimentWeights = smoothWindow.map((weight, i) => {
      const leftValue = getSentimentPositiveFromMessage(orderedMessages[idx - i]);
      const rightValue = getSentimentPositiveFromMessage(orderedMessages[idx - i]);
      const avgValue = ((leftValue || rightValue) + (rightValue || leftValue)) / 2;
      return avgValue * weight;
    });
    const smoothedPositiveSentiment = _.sum(smoothedPositiveSentimentWeights) / _.sum(smoothWindow);

    return {
      ...msg,
      is_customer,
      is_rep,
      emotion: summary.emotion,
      sentiment: summary.sentiment,
      is_issue: summary.is_issue,
      is_customer_issue: summary.is_issue && is_customer,
      is_pitch: summary.is_pitch,
      is_rep_pitch: summary.is_pitch && is_rep,
      is_question: summary.is_question,
      is_rep_question: summary.is_question && is_rep,
      is_customer_question: summary.is_question && is_customer,
      is_unsure: summary.is_unsure,
      speaker_type: msg.speaker.speaker_type,
      duration: msg.end_time_seconds - msg.start_time_seconds,
      summary: {
        ...summary,
        prob_sentiment_positive_smoothed: smoothedPositiveSentiment,
      },
    };
  });
};
